import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import { storage } from '../core/Storage';
import { audioManager } from '../core/CoreManagerAudio';
import { initLocalization, t } from '../i18n/Localization';

// Social media URLs
const SOCIAL_URLS = {
  facebook: 'https://www.facebook.com/imbasiongame',
  discord: 'https://discord.gg/UQqmcYnGx6',
  gamaru: 'https://gamaru.studio',
};

export class MainMenu extends Scene {
  private background: GameObjects.Image | null = null;
  private logo: GameObjects.Image | null = null;
  private cow: GameObjects.Image | null = null;
  private ufo: GameObjects.Image | null = null;
  private beam: GameObjects.Image | null = null;
  private ufoBaseY: number = 0;
  private beamBaseScale: number = 1;
  private soundIcon: GameObjects.Image | null = null;
  private socialButton: GameObjects.Image | null = null;
  private socialPanel: GameObjects.Container | null = null;
  private isMuted: boolean = false;
  private previousMusicVolume: number = 1;
  private previousSfxVolume: number = 1;
  private scaleRatio: number = 1;
  private hasProgress: boolean = false;

  constructor() {
    super('MainMenu');
  }

  init(): void {
    this.background = null;
    this.logo = null;
    this.cow = null;
    this.ufo = null;
    this.beam = null;
  }

  async create(): Promise<void> {
    initLocalization(this);
    // Check if player has saved progress
    const progress = await storage.getProgress();
    this.hasProgress = progress.level > 1;

    // Audio toggle state (simple mute/unmute)
    this.previousMusicVolume = audioManager.getMusicVolume() || 1;
    this.previousSfxVolume = audioManager.getSfxVolume() || 1;
    this.isMuted = audioManager.getMusicVolume() === 0 && audioManager.getSfxVolume() === 0;

    this.createUI();
    this.startMusic();

    // ENTER key starts new game (goes to level select)
    this.input.keyboard?.on('keydown-ENTER', () => {
      this.startNewGame();
    });

    // F10 abre el editor (debug)
    this.input.keyboard?.on('keydown-F10', () => {
      this.openEditor();
    });
  }

  override update(time: number): void {
    // Sine animation for UFO and beam pulse (synced)
    const period = 3000; // 3 seconds
    const t = (time / period) * Math.PI * 2;

    if (this.ufo) {
      const magnitude = 30 * this.scaleRatio;
      this.ufo.y = this.ufoBaseY + Math.sin(t) * magnitude;
    }

    if (this.beam) {
      const pulse = 1 + Math.sin(t - 0.2) * 0.03;
      this.beam.setScale(this.beamBaseScale * pulse);
      this.beam.setAlpha(0.85 + Math.sin(t - 0.2) * 0.08);
    }
  }

  private startMusic(): void {
    // Initialize CoreManagerAudio with game instance if not done
    if (this.game) {
      audioManager.init(this.game);
    }
    // Play music for MainMenu scene
    audioManager.playMusicForScene('MainMenu');
  }

  private playButtonSound(type: 'hover' | 'click'): void {
    audioManager.playUISound(type, this);
  }

  private createUI(): void {
    const { width, height } = this.scale;
    this.scaleRatio = Math.min(width / 1600, height / 900);

    // Background - centered and scaled to cover entire screen
    this.background = this.add.image(width / 2, height / 2, 'menu_v2_bg');
    const bgScaleX = width / this.background.width;
    const bgScaleY = height / this.background.height;
    const bgScale = Math.max(bgScaleX, bgScaleY);
    this.background.setScale(bgScale);

    // Beam (gives the main glow)
    const beamX = width * 0.634;
    const beamY = height * 0.12;
    this.beam = this.add.image(beamX, beamY, 'menu_v2_beam');
    this.beam.setOrigin(0.5, 0);
    this.beamBaseScale = this.scaleRatio * 1.05;
    this.beam.setScale(this.beamBaseScale);
    this.beam.setAlpha(0.9);
    this.beam.setBlendMode(Phaser.BlendModes.SCREEN);
    this.beam.setDepth(0);

    // UFO (floats a bit)
    const ufoX = width * 0.78;
    this.ufoBaseY = height * 0.06;
    this.ufo = this.add.image(ufoX, this.ufoBaseY, 'menu_v2_ufo');
    this.ufo.setOrigin(0.5, 0);
    this.ufo.setScale(this.scaleRatio * 1.2);
    this.ufo.setDepth(1);

    // Cow (foreground, bottom-left)
    this.cow = this.add.image(0, height, 'menu_v2_cow');
    this.cow.setOrigin(0, 1);
    this.cow.setScale(this.scaleRatio * 1.3);
    this.cow.setDepth(1);

    // Logo (top)
    this.logo = this.add.image(width / 4, height * 0.18, 'menu_v2_logo');
    this.logo.setScale(this.scaleRatio * 1.8);
    this.logo.setDepth(2);

    // Top-right icons: Settings and Sound
    this.createTopRightIcons(width, this.scaleRatio);

    // Menu buttons (V2 art)
    const buttonStartY = height * 0.50 + 36 * this.scaleRatio;
    const buttonSpacing = 95 * this.scaleRatio;
    const buttonScale = 0.35 * this.scaleRatio;

    // New Game button
    this.createSpriteButton(
      width / 2,
      buttonStartY,
      {
        off: 'menu_v2_btn_start_off',
        on: 'menu_v2_btn_start_on',
        selected: 'menu_v2_btn_start_selected',
        pressed: 'menu_v2_btn_start_pressed',
      },
      buttonScale,
      () => this.startNewGame()
    );

    // Continue button
    this.createSpriteButton(
      width / 2,
      buttonStartY + buttonSpacing,
      {
        off: 'menu_v2_btn_continue_off',
        on: 'menu_v2_btn_continue_on',
        selected: 'menu_v2_btn_continue_selected',
        pressed: 'menu_v2_btn_continue_pressed',
      },
      buttonScale,
      () => this.continueGame(),
      { disabled: !this.hasProgress }
    );

    // Exit button (decorative for now)
    this.createSpriteButton(
      width / 2,
      buttonStartY + buttonSpacing * 2,
      {
        off: 'menu_v2_btn_exit_off',
        on: 'menu_v2_btn_exit_on',
        selected: 'menu_v2_btn_exit_selected',
        pressed: 'menu_v2_btn_exit_pressed',
      },
      buttonScale,
      () => this.exitGame(),
      { decorative: true }
    );

    // Tutorial button (texto simple)
    this.createTutorialButton(width / 2, buttonStartY + buttonSpacing * 3, this.scaleRatio);
    this.createGddMenuShortcuts(width / 2, buttonStartY + buttonSpacing * 3 + 56 * this.scaleRatio, this.scaleRatio);

    // Social button (opens FB/Discord)
    this.createSocialIcons(width, height, this.scaleRatio);

    // Gamaru logo (pop-in)
    this.createGamaruLogo(width, height, this.scaleRatio);
  }

  private createSpriteButton(
    x: number,
    y: number,
    textures: { off: string; on: string; selected: string; pressed?: string },
    scaleRatio: number,
    onClick: () => void,
    options: { disabled?: boolean; decorative?: boolean } = {}
  ): GameObjects.Image {
    const btn = this.add.image(x, y, textures.off).setOrigin(0.5);
    const baseScale = scaleRatio;
    btn.setScale(baseScale);
    btn.setDepth(2); // Above beam and background

    const hitArea = this.add
      .zone(x, y, btn.width * baseScale, btn.height * baseScale)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: !options.disabled && !options.decorative });

    const syncHitAreaToButton = (scale: number) => {
      hitArea.setSize(btn.width * scale, btn.height * scale);
    };

    if (options.disabled || options.decorative) {
      btn.setAlpha(options.disabled ? 0.4 : 1);
      if (options.disabled) {
        hitArea.disableInteractive();
      }
      return btn;
    }

    hitArea.on('pointerover', () => {
      btn.setTexture(textures.on);
      btn.setScale(baseScale * 1.03);
      syncHitAreaToButton(baseScale * 1.03);
      this.playButtonSound('hover');
    });

    hitArea.on('pointerout', () => {
      btn.setTexture(textures.off);
      btn.setScale(baseScale);
      syncHitAreaToButton(baseScale);
    });

    hitArea.on('pointerdown', () => {
      btn.setTexture(textures.pressed ?? textures.selected);
      btn.setScale(baseScale * 0.96);
      syncHitAreaToButton(baseScale * 0.96);
      this.playButtonSound('click');
    });

    hitArea.on('pointerup', () => {
      btn.setTexture(textures.on);
      btn.setScale(baseScale * 1.03);
      syncHitAreaToButton(baseScale * 1.03);
      onClick();
    });

    return btn;
  }

  private createGddMenuShortcuts(x: number, y: number, scaleRatio: number): void {
    const entries = [
      { text: t('menu.credits'), fn: () => this.scene.start('Credits') },
    ];

    const spacing = 180 * scaleRatio;
    entries.forEach((entry, index) => {
      const label = this.add.text(x + (index - 1) * spacing, y, entry.text, {
        fontFamily: 'Arial Black',
        fontSize: `${20 * scaleRatio}px`,
        color: '#ffffff',
        stroke: '#0b0f16',
        strokeThickness: 3,
      }).setOrigin(0.5).setDepth(3);

      label.setInteractive({ useHandCursor: true });
      label.on('pointerover', () => {
        label.setColor('#9fffd0');
        this.playButtonSound('hover');
      });
      label.on('pointerout', () => label.setColor('#ffffff'));
      label.on('pointerdown', () => {
        this.playButtonSound('click');
        entry.fn();
      });
    });
  }

  private createTutorialButton(x: number, y: number, scaleRatio: number): void {
    const label = this.add.text(x, y, t('menu.tutorial'), {
      fontFamily: 'Crackvetica, Arial Black',
      fontSize: `${28 * scaleRatio}px`,
      color: '#ffffff',
      stroke: '#0b0f16',
      strokeThickness: 4,
    }).setOrigin(0.5);

    label.setInteractive({ useHandCursor: true });
    label.on('pointerover', () => {
      label.setColor('#ffd34d');
      this.playButtonSound('hover');
    });
    label.on('pointerout', () => label.setColor('#ffffff'));
    label.on('pointerdown', () => {
      this.playButtonSound('click');
      this.scene.start('Tutorial');
    });

    const trophies = this.add.text(x, y + 42 * scaleRatio, t('menu.trophies'), {
      fontFamily: 'Arial Black',
      fontSize: `${20 * scaleRatio}px`,
      color: '#ffffff',
      stroke: '#0b0f16',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(3);

    trophies.setInteractive({ useHandCursor: true });
    trophies.on('pointerover', () => {
      trophies.setColor('#9fffd0');
      this.playButtonSound('hover');
    });
    trophies.on('pointerout', () => trophies.setColor('#ffffff'));
    trophies.on('pointerdown', () => {
      this.playButtonSound('click');
      this.scene.start('Trophies');
    });
  }

  private setupButtonInteractive(btn: GameObjects.Image, scale: number): void {
    btn.setScale(scale);
    // Use a full rectangular hit area so clicks work on the whole button.
    btn.setInteractive(
      new Phaser.Geom.Rectangle(-btn.width * 0.5, -btn.height * 0.5, btn.width, btn.height),
      Phaser.Geom.Rectangle.Contains,
      { useHandCursor: true }
    );
  }

  private refreshButtonInteractive(btn: GameObjects.Image | null | undefined): void {
    if (!btn) return;
    // Re-apply full rectangular hit area if the texture size or scale changes
    btn.disableInteractive();
    btn.setInteractive(
      new Phaser.Geom.Rectangle(-btn.width * 0.5, -btn.height * 0.5, btn.width, btn.height),
      Phaser.Geom.Rectangle.Contains,
      { useHandCursor: true }
    );
  }

  private openEditor(): void {
    // Abre el editor en una pestaña nueva (debug)
    if (typeof window !== 'undefined') {
      const isLocal =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
      const url = isLocal ? 'http://localhost:5174/' : '/editor/';
      window.open(url, '_blank');
    }
  }

  private createTopRightIcons(width: number, scaleRatio: number): void {
    const iconY = 50 * scaleRatio;
    const iconScale = 0.15 * scaleRatio;
    const iconSpacing = 55 * scaleRatio;
    const rightMargin = 120 * scaleRatio;
    const iconX = width - rightMargin;

    // Settings icon (V2)
    const settingsIcon = this.add.image(iconX, iconY, 'menu_v2_icon_settings_off');
    this.setupButtonInteractive(settingsIcon, iconScale);
    settingsIcon.on('pointerover', () => {
      settingsIcon.setTexture('menu_v2_icon_settings_on');
      settingsIcon.setScale(iconScale * 1.05);
      this.refreshButtonInteractive(settingsIcon);
      this.playButtonSound('hover');
    });
    settingsIcon.on('pointerout', () => {
      settingsIcon.setTexture('menu_v2_icon_settings_off');
      settingsIcon.setScale(iconScale);
      this.refreshButtonInteractive(settingsIcon);
    });
    settingsIcon.on('pointerdown', () => {
      settingsIcon.setScale(iconScale * 0.95);
      this.playButtonSound('click');
      this.openSettings();
    });
    settingsIcon.on('pointerup', () => settingsIcon.setScale(iconScale));

    // Sound toggle (V2, no sliders)
    this.soundIcon = this.add.image(iconX - iconSpacing, iconY, 'menu_v2_icon_sound_off');
    this.setupButtonInteractive(this.soundIcon, iconScale);
    this.soundIcon.on('pointerover', () => {
      this.soundIcon?.setTexture('menu_v2_icon_sound_on');
      this.soundIcon?.setScale(iconScale * 1.05);
      this.refreshButtonInteractive(this.soundIcon);
      this.playButtonSound('hover');
    });
    this.soundIcon.on('pointerout', () => {
      this.updateSoundIcon();
      this.soundIcon?.setScale(iconScale);
    });
    this.soundIcon.on('pointerdown', () => {
      this.soundIcon?.setScale(iconScale * 0.95);
      this.playButtonSound('click');
      this.toggleSound();
    });
    this.soundIcon.on('pointerup', () => this.soundIcon?.setScale(iconScale));
    this.updateSoundIcon();
  }

  private createSocialIcons(width: number, height: number, scaleRatio: number): void {
    // Social button + popup panel (V2)
    const iconY = height * 0.08;
    const iconScale = 0.18 * scaleRatio;
    const startX = 80 * scaleRatio;

    this.socialButton = this.add.image(startX, iconY, 'menu_v2_icon_social_off');
    this.setupButtonInteractive(this.socialButton, iconScale);
    this.socialButton.on('pointerover', () => {
      this.socialButton?.setTexture('menu_v2_icon_social_on');
      this.refreshButtonInteractive(this.socialButton);
      this.playButtonSound('hover');
    });
    this.socialButton.on('pointerout', () => {
      this.socialButton?.setTexture('menu_v2_icon_social_off');
      this.refreshButtonInteractive(this.socialButton);
    });
    this.socialButton.on('pointerdown', () => {
      this.playButtonSound('click');
      this.toggleSocialPanel();
    });

    const panelX = startX;
    const panelY = iconY + 50 * scaleRatio;
    const fbIcon = this.add.image(0, 0, 'icon_facebook').setScale(0.6 * scaleRatio);
    const dcIcon = this.add.image(0, 50 * scaleRatio, 'icon_discord').setScale(0.6 * scaleRatio);

    fbIcon.setInteractive({ useHandCursor: true });
    dcIcon.setInteractive({ useHandCursor: true });

    fbIcon.on('pointerdown', () => window.open(SOCIAL_URLS.facebook, '_blank'));
    dcIcon.on('pointerdown', () => window.open(SOCIAL_URLS.discord, '_blank'));

    this.socialPanel = this.add.container(panelX, panelY, [fbIcon, dcIcon]);
    this.socialPanel.setVisible(false);
    this.socialPanel.setAlpha(0);
  }
  private createGamaruLogo(width: number, height: number, scaleRatio: number): void {
    const logoX = width * 0.9;
    const logoY = height * 0.88;
    const logoScale = 0.15 * scaleRatio;

    const gamaruLogo = this.add.image(logoX, logoY, 'gamaru_logo');
    gamaruLogo.setScale(0);
    gamaruLogo.setInteractive({ useHandCursor: true });

    this.tweens.add({
      targets: gamaruLogo,
      scale: logoScale,
      duration: 400,
      delay: 300,
      ease: 'Back.easeOut',
    });

    gamaruLogo.on('pointerover', () => {
      this.tweens.add({
        targets: gamaruLogo,
        scale: logoScale * 1.15,
        duration: 150,
        ease: 'Power2',
      });
      this.playButtonSound('hover');
    });

    gamaruLogo.on('pointerout', () => {
      this.tweens.add({
        targets: gamaruLogo,
        scale: logoScale,
        duration: 150,
        ease: 'Power2',
      });
    });

    gamaruLogo.on('pointerdown', () => {
      this.playButtonSound('click');
      window.open(SOCIAL_URLS.gamaru, '_blank');
    });
  }

  private openSettings(): void {
    this.scene.start('Configuration');
  }

  private startNewGame(): void {
    // Stop music when leaving menu
    audioManager.stopMusic();
    void storage.saveProgress(0, 1);
    this.scene.start('LevelSelect');
  }

  private continueGame(): void {
    // Stop music when leaving menu
    audioManager.stopMusic();
    this.scene.start('LevelSelect');
  }

  private exitGame(): void {
    if (typeof window !== 'undefined') {
      window.location.href = 'about:blank';
    }
  }

  private toggleSound(): void {
    // Simple mute/unmute
    if (!this.isMuted) {
      this.previousMusicVolume = audioManager.getMusicVolume() || this.previousMusicVolume || 1;
      this.previousSfxVolume = audioManager.getSfxVolume() || this.previousSfxVolume || 1;
      audioManager.setMusicVolume(0);
      audioManager.setSfxVolume(0);
      this.isMuted = true;
    } else {
      audioManager.setMusicVolume(this.previousMusicVolume || 1);
      audioManager.setSfxVolume(this.previousSfxVolume || 1);
      this.isMuted = false;
    }
    this.updateSoundIcon();
  }

  private updateSoundIcon(): void {
    // Show muted state with alpha
    if (!this.soundIcon) return;
    this.soundIcon.setTexture('menu_v2_icon_sound_off');
    this.soundIcon.setAlpha(this.isMuted ? 0.5 : 1);
  }

  private toggleSocialPanel(): void {
    // Quick show/hide for socials
    if (!this.socialPanel) return;
    const shouldShow = !this.socialPanel.visible;
    this.socialPanel.setVisible(true);
    this.tweens.add({
      targets: this.socialPanel,
      alpha: shouldShow ? 1 : 0,
      duration: 180,
      ease: 'Power2',
      onComplete: () => {
        if (!shouldShow) {
          this.socialPanel?.setVisible(false);
        }
      },
    });
  }
  
}
